import { type RGBAColor } from '@deck.gl/core'
import { makeAutoObservable } from 'mobx'
import EnekaDefinitions from '../preset.json'
import { isMobile } from 'react-device-detect'
import * as d3 from 'd3'
import { type ViewState } from 'react-map-gl/src/mapbox/mapbox'
import {
  type PresetLayer,
  type ThemeElement,
  type WktGeometryData
} from '../Pages/MapPage/@types'
import geoJSONConverter from 'wellknown'
import type AppStore from './AppStore'
import { FeatureType } from './features'
import { type Address } from './@types'

const OPERATORS = {
  '<': function (a: number, b: number) {
    return a < b
  },
  '<=': function (a: number, b: number) {
    return a <= b
  },
  '>=': function (a: number, b: number) {
    return a >= b
  },
  '>': function (a: number, b: number) {
    return a > b
  },
  '/': function (a: number, b: number) {
    return a / b
  },
  '+': function (a: number, b: number) {
    return a + b
  }
}

class ViewStore {
  public viewState: any

  public quarterDrawMode: boolean = false
  public consumptionDrawModus: boolean = false
  public showQuarterDrawMode: boolean = false
  public showConsumptionDrawMode: boolean = false
  public onQuarterClickHomepage: boolean = false
  public showEditWarning: boolean = false
  public showLegend: boolean = true
  public building3dVision: boolean = false
  public monitorVariablesChanged: boolean = false
  public editModeFeatureChange: boolean = false
  public menuOpen: '' | 'ObjectDrawControl' | 'SearchControl' = ''
  public projectAreaPuffer = 0.01
  public themeTree = EnekaDefinitions.themes
  public quarterAreaPoints: number[] = []
  public consumptionPoints: any[] = []
  public selectedThemes: ThemeElement[] = []
  public showModalRequest: boolean = false
  public isEditWarning: boolean = false
  public initialViewState: ViewState | undefined = undefined
  public addressSuggestion: Address[] = []
  public selectedAddress: Address | undefined = undefined
  public homePageViewState: any = undefined
  constructor(private readonly appStore: AppStore) {
    makeAutoObservable(this, {
      handleLineColorShow: false,
      handleColorShow: false,
      hexToRgb: false,
      themeTree: false,
      rgbToHex: false,
      calculateColor: false,
      lerpMultiColors: false
    })
  }

  setFeatureChangeOnEdit(value: boolean): void {
    this.editModeFeatureChange = value
  }

  convertCoordinatesToWktGeometry(
    coordinates,
    wktGeometryData: WktGeometryData
  ) {
    const coordinateObject = {
      type: 'Feature',
      properties: {},

      geometry: {
        type: wktGeometryData.type,
        coordinates: [coordinates]
      }
    }
    return geoJSONConverter.stringify(coordinateObject as any)
  }

  setMonitorVariablesChanged(monitorVariablesChanged: boolean): void {
    this.monitorVariablesChanged = monitorVariablesChanged
  }

  setBuilding3dVision(building3dVision: boolean) {
    this.building3dVision = building3dVision
  }

  setAddressSuggestion(addresses: Address[]): void {
    this.addressSuggestion = addresses
  }

  setSelectedAddress(address: Address | undefined): void {
    this.selectedAddress = address
  }

  setViewState(viewState): void {
    this.viewState = { ...viewState }
  }

  setHomePageViewState(homePageViewState): void {
    this.homePageViewState = { ...homePageViewState }
  }

  setShowModalRequest(showModalRequest: boolean): void {
    this.showModalRequest = showModalRequest
  }

  setIsEditWarning(isEditWarning: boolean): void {
    this.isEditWarning = isEditWarning
  }

  setShowEditWarning(showEditWarning: boolean): void {
    this.showEditWarning = showEditWarning
  }

  setMenuOpen(menuOpen: '' | 'ObjectDrawControl' | 'SearchControl'): void {
    this.menuOpen = menuOpen
  }

  getColorAsLegend(value: any, label: string) {
    let layer: PresetLayer = {}

    if ((Number(value) === 0 || isNaN(value)) && label !== 'Sanierungsstand') {
      return {
        backgroundColor: '#EEEFF3',
        color: '#003A67'
      }
    }
    switch (label) {
      case 'Treibhausgasemissionen':
        layer =
          this.themeTree.Allgemein['Gesamt - Emissionen'].layer
            .buildingsimulation

        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color:
            value === 0 || isNaN(value) || (value > 50 && value <= 150)
              ? '#003A67'
              : '#ffffff'
        }
      case 'Energiekosten':
        layer =
          this.themeTree.Allgemein['Gesamt - Energiekosten'].layer
            .buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color:
            value === 0 || isNaN(value) || value <= 20000
              ? '#003A67'
              : '#ffffff'
        }
      case 'Wärme - Kosten':
        layer =
          this.themeTree['Wärme']['Wärme - Kosten'].layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color:
            value === 0 || isNaN(value) || value <= 20000
              ? '#003A67'
              : '#ffffff'
        }
      case 'Wärmebedarf pro m²':
        layer = this.themeTree['Wärme']['Wärmebedarf'].layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>=', '<') + ')',
          color:
            value === 0 || isNaN(value) || (value > 100 && value < 160)
              ? '#003A67'
              : '#ffffff'
        }
      case 'Wärmeverbrauch':
        layer =
          this.themeTree['Wärme']['Wärmeverbrauch'].layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color:
            value === 0 || isNaN(value) || value <= 200000
              ? '#003A67'
              : '#ffffff'
        }
      case 'Wärmebezogenes Biomasse-/Biogaspotenzial':
        layer =
          this.themeTree['Wärme']['Biomassepotenzial - Wärme'].layer
            .vegetationsimulation
        return {
          backgroundColor:
            'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color: '#ffffff'
        }
      case 'Sanierungspotenzial':
        layer =
          this.themeTree['Wärme'].Sanierungspotenzial.layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color: '#003A67'
        }
      case 'Sanierungskosten':
        layer =
          this.themeTree['Wärme'].Sanierungskosten.layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color:
            value === 0 || isNaN(value) || value <= 100000
              ? '#003A67'
              : '#ffffff'
        }
      case 'Sanierungsstand':
        layer = this.themeTree['Wärme'].Sanierungsstand.layer.buildingsimulation
        const mapping =
          EnekaDefinitions.object.Sanierungsstand.renovationRelations
        const colorKey = Object.keys(mapping).find(
          (key) => mapping[key] === value
        )
        return {
          backgroundColor: 'rgb(' + layer.color[colorKey || 0].join(',') + ')',
          color: '#ffffff'
        }
      case 'Strombedarf pro m²':
        layer = this.themeTree.Strom.Strombedarf.layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color:
            value === 0 || isNaN(value) || value < 100 ? '#003A67' : '#ffffff'
        }
      case 'Stromverbrauch':
        layer = this.themeTree.Strom.Stromverbrauch.layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color: value < layer.maxvalue / 2 ? '#003A67' : '#ffffff'
        }
      case 'Strombezogenes Biomasse-/Biogaspotenzial':
        layer =
          this.themeTree.Strom['Biomassepotenzial - Strom'].layer
            .vegetationsimulation
        return {
          backgroundColor:
            'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color: '#003A67'
        }
      case 'Strom - Kosten':
        layer = this.themeTree.Strom['Strom - Kosten'].layer.buildingsimulation
        return {
          backgroundColor:
            value === 0 || isNaN(value)
              ? '#EEEFF3'
              : 'rgb(' + this.calculateColor(value, layer, '>', '<=') + ')',
          color:
            value === 0 || isNaN(value) || value <= 10000
              ? '#003A67'
              : '#ffffff'
        }
      default:
        return { backgroundColor: '#EEEFF3', color: '#003A67' }
    }
  }

  getFillColorForObjects(
    f: any,
    objectType: string,
    layer,
    layerVisible: boolean
  ) {
    if (this.appStore.isFeatureInFocus(objectType, f.properties.uuid)) {
      return (
        EnekaDefinitions.object[objectType].selection.fillcolor || [0, 0, 0, 0]
      )
    }
    if (objectType === FeatureType.Building && !layerVisible) {
      return EnekaDefinitions.object.DummyLayer.default.fillcolor
    }

    return layerVisible
      ? this.handleColorShow(
          f.properties,
          layer.singleLayer,
          layer.themeElement
        )
      : [0, 0, 0, 255]
  }

  get themeString(): string {
    return this.selectedThemes
      .map((theme) => theme.singleTheme + theme.subTheme)
      .join()
  }

  getLineColorForObjects = (
    f: any,
    objectType: string,
    layer,
    layerVisible: boolean
  ) => {
    if (this.appStore.isFeatureInFocus(objectType, f.properties.uuid)) {
      return (
        EnekaDefinitions.object[objectType].selection.linecolor || [0, 0, 0, 0]
      )
    }
    if (objectType === FeatureType.Building && !layerVisible) {
      return EnekaDefinitions.object.DummyLayer.default.linecolor
    }
    return layerVisible
      ? this.handleLineColorShow(
          f.properties,
          layer.singleLayer,
          layer.themeElement
        )
      : [0, 0, 0, 255]
  }

  hexToRgb(hex): RGBAColor {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16)
        ]
      : [0, 0, 0, 0]
  }

  getColorDomainForHeatmap(heatmap) {
    if (isMobile) return null
    if (
      heatmap?.themeElement?.layer?.buildingsimulation?.maxvalue !== undefined
    ) {
      return [
        heatmap.color ? Object.keys(heatmap.color)[0] : 0,
        heatmap.themeElement.layer.buildingsimulation.maxvalue
      ]
    }
    if (heatmap?.themeElement?.layer?.heatPotential?.maxvalue !== undefined) {
      return [
        heatmap.color ? Object.keys(heatmap.color)[0] : 0,
        heatmap.themeElement.layer.heatPotential.maxvalue
      ]
    }
    if (heatmap.maxvalue) {
      return [
        heatmap.color ? Object.keys(heatmap.color)[0] : 0,
        heatmap.maxvalue
      ]
    }
    return [
      heatmap.color ? Object.keys(heatmap.color)[0] : 0,
      heatmap.color
        ? Object.keys(heatmap.color)[Object.keys(heatmap.color).length - 1]
        : 0
    ]
  }

  getLayerVisibilityZoomValueByTheme(themeObj) {
    if (themeObj?.layer) {
      const tmp: any = Object.values(themeObj.layer)?.[0]
      return tmp.heatmap ? 13.2 : 10.0
    }
    return 10.0
  }

  handleLineColorShow = (properties, singleLayer, themeElement) => {
    const subTheme = themeElement?.subTheme

    if (themeElement === undefined) return [0, 0, 0, 0]
    const layer =
      this.themeTree[themeElement?.singleTheme][subTheme].layer[singleLayer]

    if (subTheme === 'Quartiere') {
      if (properties.color) {
        const color = this.hexToRgb(properties.color)
        color.push(100)
        return color
      }
      return this.themeTree.Allgemein.Quartiere.layer.quarter.color.border
    }
    if (layer?.borderColor) {
      return layer.borderColor
    }
    return [0, 0, 0, 0]
  }

  lerpMultiColors(colors: {}, min: number, max: number, current: number) {
    const colorlen = Object.keys(colors).length
    const steps = (max - min) / colorlen
    let colorMin = colors[colorlen - 1]
    let colorMax = colors[colorlen]
    const currColor: number = current >= max ? max : current

    for (const color in colors) {
      if (parseInt(color) === 1) {
        continue
      }
      if (currColor <= steps * parseInt(color)) {
        colorMin = colors[parseInt(color) - 1]
        colorMax = colors[color]
        break
      }
    }
    return this.lerpColor(
      this.rgbToHex(colorMin),
      this.rgbToHex(colorMax),
      currColor / max
    )
  }

  rgbToHex(color) {
    return d3.color(`rgb(${color.join(',')})`)?.formatHex()!
  }

  calculateColor(
    selectedValue: number,
    layer,
    operatorGreatest: string,
    operator: string,
    selectedValueCannotBeZero?: boolean
  ) {
    if (layer?.color) {
      const calculatedValues = Object.keys(layer.color)
      if (selectedValue === Infinity) {
        return layer.color[calculatedValues[0]].flat()
      }
      if (
        (selectedValueCannotBeZero && Number(selectedValue) === 0) ||
        isNaN(selectedValue)
      ) {
        return [196, 199, 204]
      }
      if (
        OPERATORS[operatorGreatest](
          selectedValue,
          parseInt(calculatedValues[calculatedValues.length - 1]) - 1
        )
      ) {
        return layer.color[calculatedValues[calculatedValues.length - 1]].flat()
      } else {
        for (const value of calculatedValues) {
          if (
            OPERATORS[operator](selectedValue, Number(value)) &&
            layer.color[value]
          ) {
            return layer.color[value].flat()
          }
        }

        return [0, 0, 0, 0]
      }
    }
  }

  calculatedColorBetween(
    selectedValue: number,
    layer,
    operatorGreatest: string,
    operator: string
  ) {
    if (layer?.color) {
      const calculatedValues = Object.keys(layer.color)
      if (selectedValue === Infinity) {
        return layer.color[calculatedValues[0]].flat()
      }
      if (
        selectedValue >= parseInt(calculatedValues[0]) &&
        selectedValue < parseInt(calculatedValues[1])
      ) {
        return layer.color[calculatedValues[0]].flat()
      }
      for (let i = 0; i < calculatedValues.length - 1; i++) {
        if (
          OPERATORS[operator](selectedValue, Number(calculatedValues[i + 1])) &&
          OPERATORS[operatorGreatest](
            selectedValue,
            Number(calculatedValues[i])
          )
        ) {
          return layer.color[calculatedValues[i]].flat()
        }
      }

      if (
        OPERATORS[operatorGreatest](
          selectedValue,
          parseInt(calculatedValues[calculatedValues.length - 1]) - 1
        )
      ) {
        return layer.color[calculatedValues[calculatedValues.length - 1]].flat()
      }
    }
    return [0, 0, 0, 0]
  }

  lerpColor(a: string, b: string, amount: number) {
    const color = d3.color(d3.interpolate(a, b)(amount)) as any
    return [color.r, color.g, color.b]
  }

  handleColorShow = (properties, singleLayer, themeElement) => {
    let color = [0, 0, 0, 1]
    if (themeElement === undefined || singleLayer === undefined) return color
    const subTheme = themeElement?.subTheme
    const layer =
      EnekaDefinitions.themes[themeElement?.singleTheme][subTheme].layer[
        singleLayer
      ]

    const apivariableArray = layer.apivariable.split(',')

    switch (subTheme) {
      case 'Quartiere':
        if (properties.color) {
          const color = this.hexToRgb(properties.color)
          color.push(80)
          return color
        }
        return layer.color.default.flat()
      case 'BISKO':
      case 'Feldblöcke':
        if (layer?.color?.[properties[layer.apivariable]]) {
          color = layer.color[properties[layer.apivariable]].flat()
        } else {
          color = layer.color.default.flat()
        }
        return color

      case 'Gesamt - Energiekosten':
      case 'Gesamt - Emissionen':
        let sum = 0
        for (const singleVariable in apivariableArray) {
          sum += properties[apivariableArray[singleVariable].trim()]
        }
        return sum === 0
          ? [0, 0, 0, 0]
          : this.calculateColor(sum, layer, '>', '<=', true)
      case 'Sanierungsstand':
      case 'Wärmeversorgung':
      case 'Stromversorgung':
        return layer.color[properties[layer.apivariable]].flat()

      case 'Fern-/Nahwärmepotenzial':
        return properties[layer.apivariable] > Object.keys(layer.color)[0]
          ? this.calculatedColorBetween(
              properties[layer.apivariable],
              layer,
              '>',
              '<='
            )
          : [0, 0, 0, 0]
      case 'Wärmebedarf':
        return this.calculateColor(
          properties[apivariableArray[0].trim()] /
            properties[apivariableArray[1].trim()],
          layer,
          '>=',
          '<',
          true
        )

      case 'Strom - Emissionen':
      case 'Stromverbrauch':
      case 'Strom - Kosten':
      case 'Wärme - Emissionen':
      case 'Wärme - Kosten':
      case 'Sanierungskosten':
      case 'Wärmeverbrauch':
        return this.calculateColor(
          properties[apivariableArray[0].trim()],
          layer,
          '>',
          '<=',
          true
        )

      case 'Eigenbedarf - Solarthermie':
        return this.calculateColor(
          OPERATORS['/'](
            properties[apivariableArray[0].trim()],
            properties[apivariableArray[1].trim()]
          ) * 100,
          layer,
          '>=',
          '<',
          true
        )
      case 'Eigenbedarf - Geothermie':
        if (properties.bisko === 2 || properties.bisko === 4) {
          return [0, 0, 0, 0]
        }
        const value = properties[layer.apivariable]
        return layer.color[value]
      case 'Sanierungspotenzial':
        const calcVar =
          ((properties[apivariableArray[1].trim()] -
            properties[apivariableArray[0].trim()]) /
            (properties[apivariableArray[1].trim()] > 0
              ? properties[apivariableArray[1].trim()]
              : 1)) *
          100
        return this.calculateColor(calcVar, layer, '>', '<=', true)
      case 'Strombedarf':
        return this.calculateColor(
          properties[apivariableArray[0].trim()] /
            properties[apivariableArray[1].trim()],
          layer,
          '>',
          '<=',
          true
        )

      case 'Eigenbedarf - Photovoltaik':
        return this.calculateColor(
          OPERATORS['/'](
            properties[apivariableArray[0].trim()],
            properties[apivariableArray[1].trim()]
          ) * 100,
          layer,
          '>=',
          '<',
          true
        )
      case 'Biomassepotenzial - Wärme':
      case 'Biomassepotenzial - Strom':
        return this.calculateColor(
          properties[layer.apivariable] / 1000,
          layer,
          '>',
          '<='
        )
      case 'Versorgungsanlagen': {
        const title =
          this.appStore.energyCarriers?.get(properties.carrier_id)?.title ?? ''
        return layer?.color[title]?.flat() ?? layer.color.default.flat()
      }
      default:
        return color
    }
  }
}

export default ViewStore
